import { Card, CardTitleProps } from '@boltenergy-be/design-system'
import classNames from 'classnames'
import styles from './OverviewCard.module.scss'
import { OverviewCardProps } from './types.ts'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { AnimationStates } from 'hooks/types.ts'
import Link from 'components/link/Link.tsx'
import { trackEvent } from 'utils/tracking.ts'

const Title = ({ children, variant = 'h4', ...props }: CardTitleProps) => <Card.Title {...{ variant, ...props }}>{children}</Card.Title>

const OverviewCard = forwardRef<HTMLElement, OverviewCardProps>(
  ({ button, children, className, isLoading, animation = AnimationStates.IN, setHidden, secondaryButton, ...cardProps }, ref) => {
    // Ref
    const cardRef = useRef<HTMLElement>(null)

    // Local state
    const [localAnimation, setLocalAnimation] = useState<AnimationStates | null>(
      animation === AnimationStates.NONE ? null : animation || null
    )

    // Update the local animation & trigger a re-animate on all the next siblings
    useEffect(() => {
      let delay = 0

      /**
       * Recursive function to repeat the out & in animation
       * @param {HTMLElement | null} el
       */
      const reAnimateNextSiblings = (el: HTMLElement | null) => {
        if (el && el.nodeName === 'SECTION') {
          el.classList.remove(styles['animate-in'])
          el.classList.remove(styles['animate-out'])
          void el.offsetWidth
          el.classList.add(styles['animate-out'])
          el.onanimationend = () => {
            el.style.animationDelay = `${delay * 100}ms`
            el.classList.remove(styles['animate-out'])
            el.classList.add(styles['animate-in'])
            el.onanimationend = null
            delay = delay + 1
          }

          reAnimateNextSiblings(el.nextElementSibling as HTMLElement)
        }
      }

      setLocalAnimation(animation)

      if (animation === AnimationStates.OUT) {
        reAnimateNextSiblings(cardRef?.current?.nextElementSibling as HTMLElement)
      }
    }, [animation])

    // Use to combine forwardRef with the local cardRef
    useImperativeHandle(ref, () => cardRef.current!, [])

    return (
      <Card
        as="section"
        className={classNames(
          styles.card,
          {
            [styles.loading]: isLoading,
            [styles['animate-in']]: localAnimation === AnimationStates.IN,
            [styles['animate-out']]: localAnimation === AnimationStates.OUT
          },
          className
        )}
        {...cardProps}
        ref={cardRef}
        onAnimationEnd={() => {
          if (localAnimation === AnimationStates.OUT) {
            setHidden?.()
          } else {
            setLocalAnimation(null)
          }
        }}
      >
        {children}

        {!!button && (
          <Card.Action
            loading={isLoading}
            label={button.label}
            variant={button.variant}
            representation={button.representation}
            {...(button.href
              ? {
                  href: <Link href={button.href} />,
                  onMouseDown: () => trackEvent({ category: 'overview', object: 'card', action: 'click' }, { id: button.href })
                }
              : {
                  onClick: () => {
                    trackEvent({ category: 'overview', object: 'card', action: 'click' }, { id: button.id })
                    button.onClick?.()
                  }
                })}
          />
        )}

        {!!secondaryButton && (
          <Card.Action
            loading={isLoading}
            label={secondaryButton.label}
            variant={secondaryButton.variant}
            representation={secondaryButton.representation}
            {...(secondaryButton.href
              ? {
                  href: <Link href={secondaryButton.href} />,
                  onMouseDown: () => trackEvent({ category: 'overview', object: 'card', action: 'click' }, { id: secondaryButton.href })
                }
              : {
                  onClick: () => {
                    trackEvent({ category: 'overview', object: 'card', action: 'click' }, { id: secondaryButton.id })
                    secondaryButton.onClick?.()
                  }
                })}
          />
        )}
      </Card>
    )
  }
)

OverviewCard.displayName = 'OverviewCard'
const OverviewCardWithSubcomponents = Object.assign(OverviewCard, { Title })

export default OverviewCardWithSubcomponents
